import { Button, TableProps } from "antd";
import dayjs from "dayjs";
import moment from "moment";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import CaretRight from "../../../assets/icons/CaretRight";
import SortIcon from "../../../assets/icons/SortIcon";
import UpdateSalaryModal from "../../../components/Payroll/UpdateSalaryModal";
import SuccessModal from "../../../components/Report/SuccessModal";
import ConfirmModal from "../../../components/ui/ConfirmModal";
import CustomTable from "../../../components/ui/CustomTable";
import { dateFormat, dbDateFormat } from "../../../constants/constants";
import {
  useDeletePayslipMutation,
  useGetSalarySummaryQuery,
} from "../../../features/payroll/payrollApi";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import useTable from "../../../hooks/useTable";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { toast } from "../../../lib/toast";
import { GetCommonParams } from "../../../types";
import { GetSalarySummaryResponseType } from "../../../types/payroll";
import { countParPageSerialNumber } from "../../../utils/number";
import {
  addKeyInArray,
  formatSouthAsianWithDecimals,
} from "../../../utils/utils";
import GenerateSalaryModal from "./GenerateSalaryModal";

export const payrollBetaSettingsTabList = [
  { label: "Payroll Summary", link: "/admin/payroll" },
  { label: "Pay Report", link: `/admin/payroll/pay-report` },
  { label: "TAX Report", link: "/admin/payroll/tax-report" },
  { label: "Variable Salary", link: "/admin/payroll/variable-salary" },
  {
    label: "Addition Deductions Salary",
    link: "/admin/payroll/addition-deductions-salary",
  },
  {
    label: "Overtime Salary",
    link: "/admin/payroll/overtime-salary",
  },
  {
    label: "Holiday Allowance",
    link: "/admin/payroll/holiday-allowance",
  },
];

export const payrollManagerSettingTabList = [
  { label: "Variable Salary", link: "/admin/payroll/variable-salary" },
  {
    label: "Addition Deductions Salary",
    link: "/admin/payroll/addition-deductions-salary",
  },
  {
    label: "Overtime Salary",
    link: "/admin/payroll/overtime-salary",
  },
  {
    label: "Holiday Allowance",
    link: "/admin/payroll/holiday-allowance",
  },
];

type ModalType = "CONFIRM" | "GENERATE_SALARY" | "UPLOAD_SALARY" | "SUCCESS";

export default function PayrollSummary() {
  const { page, limit, handlePagination, handleLimit } =
    useTable<GetCommonParams>();
  const { open, handleClose, handleOpen } = useModal<string, ModalType>();
  const [selectedData, setSelectedData] = useState<string[]>([]);
  const { isLoading, isError, error, data } = useGetSalarySummaryQuery({
    params: {
      page,
      limit,
    },
  });
  const [
    deletePayslip,
    {
      isLoading: isDeleteLoading,
      isError: isDeleteError,
      error: deleteError,
      isSuccess: isDeleteSuccess,
    },
  ] = useDeletePayslipMutation();
  useError(isError, error, "Get payroll summary data fail");
  useError(isDeleteError, deleteError, "Delete payroll summary report fail");

  // console.log(selectedData);

  const columns: TableProps<
    GetSalarySummaryResponseType["data"]["salaryData"][0]
  >["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: <div className="text-nowrap w-[70px]">Month</div>,
      dataIndex: "schedule_date",
      render: (val) => moment(val, dbDateFormat).format("MMM, YYYY"),
    },
    {
      title: "Generate Date",
      dataIndex: "created_at",
      render: (val) =>
        val ? moment.utc(val).local().format(dateFormat) : "n/a",
      sorter: (a, b) =>
        a && b
          ? dayjs(a?.schedule_date, dbDateFormat).valueOf() -
            dayjs(b?.schedule_date, dbDateFormat).valueOf()
          : 0,
      sortIcon: () => <SortIcon />,
    },
    {
      title: <div className="text-nowrap w-[150px]">Cycle</div>,
      render: (_, record) =>
        `${moment(record?.cycle_start_date, dbDateFormat).format(
          dateFormat
        )} - ${moment(record?.cycle_end_date, dbDateFormat).format(
          dateFormat
        )}`,
    },
    {
      title: () => (
        <span className="text-right inline-block w-[100px]">
          Total Gross <br /> Pay (BDT)
        </span>
      ),
      dataIndex: "salaryBreakdown",
      render: (val) => (
        <span className="pr-3">
          {" "}
          {formatSouthAsianWithDecimals(val?.gross || 0)}
        </span>
      ),
      sortIcon: () => <SortIcon />,
      sorter: (a, b) =>
        Number(a?.salaryBreakdown?.gross ?? 0) -
        Number(b?.salaryBreakdown?.gross ?? 0),
      align: "right",
    },
    {
      title: () => (
        <span className="text-right inline-block w-[100px]">
          Total <br /> Addition (BDT)
        </span>
      ),
      dataIndex: "salaryBreakdown",
      render: (val) => (
        <span className="pr-3">
          {" "}
          {formatSouthAsianWithDecimals(val?.addition || 0)}
        </span>
      ),
      sortIcon: () => <SortIcon />,
      sorter: (a, b) =>
        Number(a?.salaryBreakdown?.addition ?? 0) -
        Number(b?.salaryBreakdown?.addition ?? 0),
      align: "right",
    },
    {
      title: () => (
        <span className="text-right inline-block w-[100px]">
          Total <br /> Deduction (BDT)
        </span>
      ),
      dataIndex: "salaryBreakdown",
      render: (val) => (
        <span className="pr-3">
          {" "}
          {formatSouthAsianWithDecimals(val?.deduction ?? 0)}
        </span>
      ),
      sortIcon: () => <SortIcon />,
      sorter: (a, b) =>
        Number(a?.salaryBreakdown?.addition ?? 0) -
        Number(b?.salaryBreakdown?.addition ?? 0),
      align: "right",
    },
    {
      title: () => (
        <span className="text-right inline-block w-[100px]">
          Total <br /> Tax Pay (BDT)
        </span>
      ),
      dataIndex: "salaryBreakdown",
      render: (val) => (
        <span className="pr-3">
          {formatSouthAsianWithDecimals(val?.taxTotal ?? 0)}
        </span>
      ),
      sortIcon: () => <SortIcon />,
      sorter: (a, b) => Number(0) - Number(0),
      align: "right",
    },
    {
      title: "Net Pay",
      // dataIndex: "netPay",
      render: (val) =>
        formatSouthAsianWithDecimals(
          val?.salaryBreakdown?.gross +
            val?.salaryBreakdown?.addition -
            val?.salaryBreakdown?.deduction || 0
        ),
      sortIcon: () => <SortIcon />,
      sorter: (a, b) => {
        const totalSalaryBreakdownA =
          a?.salaryBreakdown?.gross +
            a?.salaryBreakdown?.addition -
            a?.salaryBreakdown?.deduction || 0;
        const totalSalaryBreakdownB =
          b?.salaryBreakdown?.gross +
            b?.salaryBreakdown?.addition -
            b?.salaryBreakdown?.deduction || 0;

        return (
          Number(totalSalaryBreakdownA ?? 0) -
          Number(totalSalaryBreakdownB ?? 0)
        );
      },
      align: "right",
    },
    {
      title: "Action",
      dataIndex: "id",
      render: (val) => (
        <Link className="px-3" to={`/admin/payroll/pay-report/view/${val}`}>
          <Button className="w-7 h-7 flex items-center justify-center">
            <CaretRight />
          </Button>
        </Link>
      ),
    },
  ];

  useEffect(() => {
    if (isDeleteSuccess) {
      setSelectedData([]);
      toast("success", "Payroll summary report removed successfully.");
      handleOpen({ type: "SUCCESS" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDeleteSuccess]);

  return (
    <SidebarLayout title="Payroll" tabList={payrollBetaSettingsTabList}>
      {open?.type === "SUCCESS" && (
        <SuccessModal
          type="queue"
          open={open?.open}
          handleClose={handleClose}
        />
      )}
      <CustomTable
        loading={isLoading}
        limit={limit}
        page={page}
        columns={columns}
        total={data?.data?.total}
        dataSource={addKeyInArray(data?.data?.salaryData)}
        handleLimit={handleLimit}
        handlePagination={handlePagination}
        rowSelection={{
          type: "checkbox",
          selectedRowKeys: selectedData,
          onChange: (key) => {
            setSelectedData(key as string[]);
          },
          columnWidth: 40,
        }}
        leftFilterSection={
          <>
            {selectedData?.length ? (
              <Button
                className="outline-button-primary"
                onClick={() =>
                  handleOpen({
                    type: "CONFIRM",
                  })
                }
              >
                Remove payroll summary report
              </Button>
            ) : null}
          </>
        }
        filterSection={
          <>
            <Button
              className="modal-button"
              type="primary"
              htmlType="submit"
              size="middle"
              style={{ minWidth: "150px" }}
              onClick={() => {
                handleOpen({ type: "GENERATE_SALARY" });
              }}
            >
              Generate Salary
            </Button>
          </>
        }
      />

      {open.type === "GENERATE_SALARY" && (
        <ConfirmModal
          open={open.open}
          handleClose={handleClose}
          confirmTitle="Upload Salary"
          questionText="Do you want to upload variable salary or addition deduction salary?"
          okText="Yes"
          cancelText="No"
          handleOk={() => {
            handleOpen({ type: "UPLOAD_SALARY" });
          }}
          handleCancel={() => {
            handleOpen({ type: "VIEW" });
          }}
        />
      )}

      {open.type === "VIEW" && (
        <GenerateSalaryModal
          open={open.open}
          handleClose={handleClose}
          loading={false}
        />
      )}

      {open.type === "UPLOAD_SALARY" && (
        <UpdateSalaryModal
          open={open.open}
          handleClose={handleClose}
          handleConfirm={() => {
            handleOpen({ type: "VIEW" });
          }}
        />
      )}

      {open?.type === "CONFIRM" ? (
        <ConfirmModal
          open={open?.open}
          handleOk={() =>
            deletePayslip({
              id: selectedData,
            })
          }
          handleClose={handleClose}
          confirmTitle="Remove Items"
          questionText="Are you sure to remove this item?"
          okText="Remove"
          okLoading={isDeleteLoading}
        />
      ) : null}
    </SidebarLayout>
  );
}
